/**
 * CatalogManager — round resource management (resource-management design).
 *
 * A user-pointed **catalog directory** (a folder/repo holding `{skills,extensions,agents,prompts,themes}`)
 * is the easy-to-setup source of a user's workflows. From it pi can:
 *  - **install** a resource = copy it into the pi user level (`<agentDir>/<kind>/`),
 *  - **update** = hash-compare what's installed against the catalog and re-sync changed/outdated ones,
 *  - **backup** = copy a user-level resource back into the catalog.
 * Per machine, the user installs only the subset they want; `update` refreshes exactly those.
 *
 * Copies are content-recursive (`cpSync`); status is decided by a recursive content hash so it is
 * portable across machines (mtimes differ; content does not).
 */

import { createHash, randomBytes } from "node:crypto";
import { cpSync, existsSync, readdirSync, readFileSync, realpathSync, renameSync, rmSync, statSync } from "node:fs";
import { join } from "node:path";

export const CATALOG_KINDS = ["skills", "extensions", "agents", "prompts", "themes"] as const;
export type CatalogKind = (typeof CATALOG_KINDS)[number];

export type CatalogStatus = "not-installed" | "up-to-date" | "outdated";

export interface CatalogEntry {
	kind: CatalogKind;
	/** Top-level name in the catalog (a directory or file name). */
	name: string;
	/** Absolute path of the entry in the catalog. */
	catalogPath: string;
	/** Absolute path where it installs at user level. */
	installPath: string;
}

export class CatalogManager {
	private readonly agentDir: string;
	private readonly catalogDir: string;

	constructor(agentDir: string, catalogDir: string) {
		this.agentDir = agentDir;
		this.catalogDir = catalogDir;
	}

	/** Every resource discovered in the catalog, across all kinds. */
	list(): CatalogEntry[] {
		const entries: CatalogEntry[] = [];
		for (const kind of CATALOG_KINDS) {
			const kindDir = join(this.catalogDir, kind);
			if (!existsSync(kindDir)) continue;
			let names: string[];
			try {
				names = readdirSync(kindDir);
			} catch {
				continue;
			}
			for (const name of names) {
				if (name.startsWith(".")) continue;
				entries.push({
					kind,
					name,
					catalogPath: join(kindDir, name),
					installPath: join(this.agentDir, kind, name),
				});
			}
		}
		return entries;
	}

	/** Status of one catalog entry relative to what is installed at user level. */
	status(entry: CatalogEntry): CatalogStatus {
		if (!existsSync(entry.installPath)) return "not-installed";
		return hashPath(entry.catalogPath) === hashPath(entry.installPath) ? "up-to-date" : "outdated";
	}

	/** Copy a catalog entry into the user level (install or overwrite), atomically. */
	install(entry: CatalogEntry): void {
		safeCopy(entry.catalogPath, entry.installPath);
	}

	/**
	 * Re-sync every INSTALLED resource that is outdated relative to the catalog (catalog → user level).
	 * Never installs things the user has not already chosen on this machine. Returns the updated entries.
	 */
	update(): CatalogEntry[] {
		const updated: CatalogEntry[] = [];
		for (const entry of this.list()) {
			if (this.status(entry) === "outdated") {
				this.install(entry);
				updated.push(entry);
			}
		}
		return updated;
	}

	/** Copy a user-level resource back into the catalog (backup). Requires it to exist at user level. */
	backup(entry: CatalogEntry): void {
		if (!existsSync(entry.installPath)) {
			throw new Error(`Cannot back up "${entry.kind}/${entry.name}": not installed at user level.`);
		}
		safeCopy(entry.installPath, entry.catalogPath);
	}
}

/**
 * Copy `src` over `dest` without ever leaving a partially-copied tree at `dest` (Bug #18): copy into a
 * sibling temp first, then swap it into place; on any failure the temp is removed and `dest` is left
 * untouched.
 */
function safeCopy(src: string, dest: string): void {
	const tmp = `${dest}.pi-tmp-${randomBytes(6).toString("hex")}`;
	try {
		cpSync(src, tmp, { recursive: true, force: true });
		rmSync(dest, { recursive: true, force: true });
		renameSync(tmp, dest);
	} catch (err) {
		try {
			rmSync(tmp, { recursive: true, force: true });
		} catch {}
		throw err;
	}
}

/**
 * Content hash of a file or directory tree: stable across machines (ignores mtimes/paths outside the
 * tree). Files contribute their relative path + bytes; directories are walked in sorted order so the
 * hash is deterministic.
 */
export function hashPath(target: string): string {
	const hash = createHash("sha256");
	const seen = new Set<string>(); // real paths already visited — breaks circular symlinks (Bug #19)
	const walk = (abs: string, rel: string, depth: number): void => {
		if (depth > 64) return; // hard depth cap as a backstop
		let real: string;
		try {
			real = realpathSync(abs);
		} catch {
			return;
		}
		if (seen.has(real)) return; // already walked (symlink loop or shared target)
		seen.add(real);
		let stats: ReturnType<typeof statSync>;
		try {
			stats = statSync(abs);
		} catch {
			return;
		}
		if (stats.isDirectory()) {
			let children: string[];
			try {
				children = readdirSync(abs).sort();
			} catch {
				return;
			}
			for (const child of children) {
				walk(join(abs, child), rel ? `${rel}/${child}` : child, depth + 1);
			}
		} else if (stats.isFile()) {
			hash.update(` ${rel} `);
			try {
				hash.update(readFileSync(abs));
			} catch {
				// unreadable file → contributes only its path
			}
		}
	};
	walk(target, "", 0);
	return hash.digest("hex");
}
